"use client";

import { EyeIcon, EyeOffIcon } from "lucide-react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { forwardRef, useState } from "react";
import { cn } from "../../lib";
import { Input } from "../input";
import {
	ariaInvalidFromError,
	buildControlDescribedBy,
} from "./field-a11y";
import {
	FieldShell,
	type FieldLayoutHint,
	type FieldLabelVariant,
} from "./field-shell";

export interface PasswordFieldProps
	extends Omit<
		InputHTMLAttributes<HTMLInputElement>,
		"type" | "value" | "onChange"
	> {
	id: string;
	label: string;
	value: string;
	onChange: (value: string) => void;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	layoutHint?: FieldLayoutHint;
	labelVariant?: FieldLabelVariant;
	labelTrailing?: ReactNode;
	hideLabel?: boolean;
	/** Required a11y label when password is hidden (caller i18n). */
	showPasswordLabel: string;
	/** Required a11y label when password is visible (caller i18n). */
	hidePasswordLabel: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
	function PasswordField(
		{
			id,
			label,
			value,
			onChange,
			description,
			error,
			reserveErrorSpace = false,
			layoutHint,
			labelVariant = "default",
			labelTrailing,
			hideLabel = false,
			showPasswordLabel,
			hidePasswordLabel,
			className,
			disabled,
			onBlur,
			name,
			autoComplete = "current-password",
			...inputProps
		},
		ref,
	) {
		const [showPassword, setShowPassword] = useState(false);
		const controlDescribedBy = buildControlDescribedBy({
			id,
			error,
			hasDescription: Boolean(description),
		});

		return (
			<FieldShell
				id={id}
				label={hideLabel ? undefined : label}
				description={description}
				error={error}
				reserveErrorSpace={reserveErrorSpace}
				layoutHint={layoutHint}
				labelVariant={labelVariant}
				labelTrailing={labelTrailing}
				className={className}
			>
				<div className="relative">
					<Input
						ref={ref}
						id={id}
						type={showPassword ? "text" : "password"}
						className="pr-10"
						value={value}
						name={name}
						disabled={disabled}
						autoComplete={autoComplete}
						onBlur={onBlur}
						onChange={(event) => onChange(event.target.value)}
						aria-label={hideLabel ? label : undefined}
						aria-invalid={ariaInvalidFromError(error)}
						aria-describedby={controlDescribedBy}
						{...inputProps}
					/>
					<button
						type="button"
						disabled={disabled}
						aria-label={
							showPassword ? hidePasswordLabel : showPasswordLabel
						}
						onClick={() => setShowPassword((previous) => !previous)}
						className={cn(
							"absolute inset-y-0 right-0 flex items-center pr-4 text-primary text-xl",
							disabled && "cursor-not-allowed opacity-50",
						)}
					>
						{showPassword ? (
							<EyeOffIcon className="size-4" aria-hidden />
						) : (
							<EyeIcon className="size-4" aria-hidden />
						)}
					</button>
				</div>
			</FieldShell>
		);
	},
);
